import * as React from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, LinkContainer } from "components/common";
import ShareButton from "components/common/ShareButton";
import Icon from "components/common/Icon";
import { ButtonToolbar, Button, DropdownButton, DeleteMenuItem } from "components/bootstrap";
import UserFilterShareModal from "search-filter/components/UserFilterShareModal";
import useSearchFilterUsagesSummaryQuery from "search-filter/hooks/useSearchFilterUsagesSummaryQuery";
import useUserSearchFilterMutation from "search-filter/hooks/useUserSearchFilterMutation";
import confirmActionUsagesAlert from "search-filter/confirmActionUsagesAlert";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import usePluggableEntitySharedActions from "hooks/usePluggableEntitySharedActions";
import usePluggableEntityTableElements from "hooks/usePluggableEntityTableElements";
import usePermissions from "hooks/usePermissions";
import SearchFiltersRoutes from "search-filter/Routes";
import useScopePermissions from "hooks/useScopePermissions";
import SearchFilterTitle from "search-filter/components/SearchFilterTitle";
const Title = ({ filter }) => {
  const { t } = useTranslation("enterprise_search");
  return /* @__PURE__ */ React.createElement(Link, { to: SearchFiltersRoutes.SHOW(filter.id) }, filter.title || t("filters_overview_item.no_title"));
};
const FiltersOverviewItem = ({ filter }) => {
  const [showFilterShareModal, setShowFilterShareModal] = useState(false);
  const { isFetching: isFetchingUsagesSummary, refetch } = useSearchFilterUsagesSummaryQuery(filter.id, {
    enabled: false
  });
  const { delete: deleteFilter } = useUserSearchFilterMutation();
  const sendTelemetry = useSendTelemetry("search-filter");
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableEntitySharedActions(filter, "search_filter");
  const { getPluggableTableCells } = usePluggableEntityTableElements(
    null,
    "search_filter"
  );
  const { t } = useTranslation("enterprise_search");
  const { isPermitted } = usePermissions();
  const { scopePermissions } = useScopePermissions(filter);
  const canEdit = (scopePermissions == null ? void 0 : scopePermissions.is_mutable) && isPermitted(`search_filters:edit:${filter.id}`);
  const canDelete = (scopePermissions == null ? void 0 : scopePermissions.is_deletable) && isPermitted(`search_filters:delete:${filter.id}`);
  const toggleShareModalOpen = useCallback(() => {
    setShowFilterShareModal((cur) => !cur);
  }, [setShowFilterShareModal]);
  const onDeleteMyFilter = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_DELETED, {
      app_pathname: "my-filters"
    });
    if (!isFetchingUsagesSummary) {
      refetch().then(({ data: { summary: usagesSummary } }) => {
        var _a;
        if ((usagesSummary == null ? void 0 : usagesSummary.length) && !confirmActionUsagesAlert({
          action: "deleting",
          usagesSummary,
          additionalExplanation: t("filters_overview_item.removal_explanation")
        })) {
          return Promise.resolve();
        }
        return (_a = deleteFilter(filter.id)) == null ? void 0 : _a.then((res) => res);
      });
    }
  }, [isFetchingUsagesSummary, refetch, deleteFilter, filter.id, sendTelemetry, t]);
  const moreActions = [
    pluggableActions.length ? pluggableActions : null,
    canDelete ? /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: onDeleteMyFilter, key: "delete-menu-item" }) : null
  ].filter(Boolean);
  return /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(SearchFilterTitle, { filter }, /* @__PURE__ */ React.createElement(Title, { filter }))), /* @__PURE__ */ React.createElement("td", null, filter.queryString), /* @__PURE__ */ React.createElement("td", null, filter.description || /* @__PURE__ */ React.createElement("i", null, t("filters_overview_item.no_description"))), /* @__PURE__ */ React.createElement("td", null, getPluggableTableCells(filter.id)), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(ShareButton, { entityType: "search_filter", entityId: filter.id, onClick: toggleShareModalOpen, bsSize: "xs" }), canEdit && /* @__PURE__ */ React.createElement(LinkContainer, { to: SearchFiltersRoutes.EDIT(filter.id) }, /* @__PURE__ */ React.createElement(Button, { bsSize: "xs" }, /* @__PURE__ */ React.createElement(Icon, { name: "edit_square" }), " ", t("filters_overview_item.edit"))), !!moreActions.length && /* @__PURE__ */ React.createElement(
    DropdownButton,
    {
      key: "more-actions-button",
      title: t("filters_overview_item.more_actions"),
      bsSize: "xs",
      pullRight: true,
      id: `more-actions-dropdown-${filter.id}`
    },
    moreActions
  )), showFilterShareModal && /* @__PURE__ */ React.createElement(UserFilterShareModal, { filter, toggleShareModalOpen }), pluggableActionModals));
};
export default FiltersOverviewItem;
